import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Category } from '@/types'

interface CategoryWithPreview extends Category {
  preview_images?: {
    primary: string | null
    secondary: string | null
  }
}

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      // Get categories with product count
      const { data: categories, error } = await supabase
        .from('categories')
        .select(`
          *,
          products:products(count)
        `)
        .order('sort_order', { ascending: true })

      if (error) throw error

      // Get first 2 products with images for each category
      const categoriesWithPreviews = await Promise.all(
        categories.map(async (cat) => {
          const { data: products } = await supabase
            .from('products')
            .select(`
              id,
              images:product_images(image_url, is_primary, sort_order)
            `)
            .eq('category_id', cat.id)
            .eq('is_active', true)
            .order('sort_order', { ascending: true })
            .limit(2)

          let primaryImage: string | null = null
          let secondaryImage: string | null = null

          if (products && products.length > 0) {
            // Sort first product's images
            const firstProductImages = (products[0].images || []).sort((a, b) => {
              if (a.is_primary && !b.is_primary) return -1
              if (!a.is_primary && b.is_primary) return 1
              return a.sort_order - b.sort_order
            })

            primaryImage = firstProductImages[0]?.image_url || null

            // Check if there's a second product
            if (products.length > 1 && products[1].images?.length > 0) {
              // Use second product's first image
              const secondProductImages = (products[1].images || []).sort((a, b) => {
                if (a.is_primary && !b.is_primary) return -1
                if (!a.is_primary && b.is_primary) return 1
                return a.sort_order - b.sort_order
              })
              secondaryImage = secondProductImages[0]?.image_url || null
            } else if (firstProductImages.length > 1) {
              // Use first product's second image
              secondaryImage = firstProductImages[1]?.image_url || null
            }
          }

          return {
            ...cat,
            product_count: cat.products?.[0]?.count ?? 0,
            preview_images: {
              primary: primaryImage,
              secondary: secondaryImage,
            },
          }
        })
      )

      return categoriesWithPreviews as CategoryWithPreview[]
    },
  })
}

export function useCategory(slug: string | undefined) {
  return useQuery({
    queryKey: ['category', slug],
    queryFn: async () => {
      if (!slug) return null

      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('slug', slug)
        .single()

      if (error) throw error
      return data as Category
    },
    enabled: !!slug,
  })
}
